import React, { useEffect, useRef, useState } from 'react';
import { getPageMeta } from './navigation';
import { IconMenu, IconChevronDown, IconLogout, IconUser, IconLink, IconLock } from './icons';
import { Modal } from './ui';
import ChangePasswordForm from '../ChangePasswordForm';

const Topbar = ({ pathname, user, onOpenMobileNav, onLogout, actions }) => {
  const meta = getPageMeta(pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (e) => {
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const initials = (user?.name || user?.email || 'A')
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className="sticky top-0 z-30 border-b border-ink-200 bg-white/85 backdrop-blur-md">
      <div className="flex min-h-[80px] flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={onOpenMobileNav}
          aria-label="Open navigation"
          className="-ml-1 rounded-lg p-2 text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 lg:hidden"
        >
          <IconMenu size={20} />
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-lg font-700 tracking-tight text-ink-900">
            {meta.title}
          </h1>
          <p className="hidden truncate text-[13px] text-ink-500 sm:block">{meta.description}</p>
        </div>

        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}

        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          title="Opens the public site in a new tab"
          className="hidden shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 sm:inline-flex"
        >
          <IconLink size={15} />
          View public site
        </a>

        <div className="relative shrink-0" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="flex items-center gap-2 rounded-lg py-1.5 pl-1.5 pr-2 text-left transition-colors hover:bg-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-800 font-display text-[11px] font-700 text-white">
              {initials}
            </span>
            <span className="hidden min-w-0 sm:block">
              <span className="block max-w-[12rem] truncate text-[13px] font-600 leading-tight text-ink-900">
                {user?.name || 'Admin'}
              </span>
              <span className="block text-[11px] capitalize leading-tight text-ink-500">
                {user?.role || 'admin'}
              </span>
            </span>
            <IconChevronDown size={15} className="text-ink-500" />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="animate-rise absolute right-0 top-[calc(100%+8px)] w-60 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-panel"
            >
              <div className="flex items-center gap-3 border-b border-ink-100 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                  <IconUser size={17} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-600 text-ink-900">{user?.name || 'Admin'}</p>
                  <p className="truncate text-xs text-ink-500">{user?.email}</p>
                </div>
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  setPasswordOpen(true);
                }}
                className="flex w-full items-center gap-2.5 border-b border-ink-100 px-4 py-3 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-50 hover:text-ink-900"
              >
                <IconLock size={17} />
                Change password
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onLogout();
                }}
                className="flex w-full items-center gap-2.5 px-4 py-3 text-sm font-medium text-ink-700 transition-colors hover:bg-danger-50 hover:text-danger-700"
              >
                <IconLogout size={17} />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        title="Change password"
        description={user?.email}
        size="md"
      >
        <ChangePasswordForm compact onDone={() => {}} />
      </Modal>
    </header>
  );
};

export default Topbar;
